import { authAPI } from './api';

/** Recursos opcionais: o billing-service retorna 404 quando não há registro. */
async function getOptional<T>(request: () => Promise<{ data: T }>): Promise<T | null> {
  try {
    const response = await request();
    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return null;
    }
    throw error;
  }
}

// Types
export interface CompanyPlan {
  id: string;
  company_id: string;
  plan_name: string;
  features: Record<string, unknown>;
  price: number;
  billing_cycle: 'monthly' | 'annual' | 'quarterly' | 'one_time';
  limits: Record<string, unknown>;
  is_active: boolean;
  started_at: string;
  ends_at?: string;
  created_at: string;
  updated_at: string;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface CompanyInvoice {
  id: string;
  company_id: string;
  plan_id?: string;
  invoice_number: string;
  amount: number;
  taxes: number;
  discount: number;
  total_amount: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
  due_date: string;
  paid_at?: string;
  items: InvoiceItem[];
  notes?: string;
  payment_method?: string;
  payment_reference?: string;
  created_at: string;
  updated_at: string;
  is_overdue?: boolean;
  days_overdue?: number;
}

export interface PaymentGateway {
  id: string;
  company_id: string;
  gateway_name: 'stripe' | 'mercado_pago' | 'pagseguro' | 'other';
  is_active: boolean;
  is_default: boolean;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface BillingPolicy {
  id: string;
  company_id: string;
  pricing_model: 'monthly' | 'annual' | 'usage_based' | 'hybrid';
  base_price: number;
  usage_rates: Record<string, unknown>;
  discounts: unknown[];
  promotions: unknown[];
  limits: Record<string, unknown>;
  grace_period_days: number;
  late_fee_percentage: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BillingAlert {
  id: string;
  company_id: string;
  invoice_id?: string;
  alert_type: 'payment_due' | 'payment_overdue' | 'payment_failed' | 'limit_warning' | 'limit_exceeded';
  message: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
}

// Plans API
export const fetchActivePlan = async (): Promise<CompanyPlan | null> => {
  return getOptional(() => authAPI.get('/billing/plans/active'));
};

export const fetchAllPlans = async (): Promise<CompanyPlan[]> => {
  const response = await authAPI.get('/billing/plans');
  return response.data ?? [];
};

export const fetchPlanHistory = async (): Promise<unknown[]> => {
  const response = await authAPI.get('/billing/plans/history');
  return response.data;
};

export const createPlan = async (plan: Partial<CompanyPlan>): Promise<CompanyPlan> => {
  const response = await authAPI.post('/billing/plans', plan);
  return response.data;
};

export const updatePlan = async (id: string, plan: Partial<CompanyPlan>): Promise<CompanyPlan> => {
  const response = await authAPI.put(`/billing/plans/${id}`, plan);
  return response.data;
};

export const deactivatePlan = async (id: string): Promise<void> => {
  await authAPI.delete(`/billing/plans/${id}`);
};

// Invoices API
export const fetchInvoices = async (params?: { status?: string; limit?: number; offset?: number }): Promise<{ invoices: CompanyInvoice[]; total: number }> => {
  const response = await authAPI.get('/billing/invoices', { params });
  return response.data;
};

export const fetchInvoice = async (id: string): Promise<CompanyInvoice> => {
  const response = await authAPI.get(`/billing/invoices/${id}`);
  return response.data;
};

export const generateInvoice = async (invoice: Partial<CompanyInvoice>): Promise<CompanyInvoice> => {
  const response = await authAPI.post('/billing/invoices', invoice);
  return response.data;
};

export const generateInvoiceFromPlan = async (): Promise<CompanyInvoice> => {
  const response = await authAPI.post('/billing/invoices/from-plan');
  return response.data;
};

export const updateInvoice = async (id: string, invoice: Partial<CompanyInvoice>): Promise<CompanyInvoice> => {
  const response = await authAPI.put(`/billing/invoices/${id}`, invoice);
  return response.data;
};

export const payInvoice = async (id: string, payment: { payment_method: string; payment_reference?: string }): Promise<CompanyInvoice> => {
  const response = await authAPI.post(`/billing/invoices/${id}/pay`, payment);
  return response.data;
};

export const fetchOverdueInvoices = async (): Promise<CompanyInvoice[]> => {
  const response = await authAPI.get('/billing/invoices/overdue');
  return response.data;
};

// Payment Gateways API
export const fetchPaymentGateways = async (): Promise<PaymentGateway[]> => {
  const response = await authAPI.get('/billing/payment-gateways');
  return response.data;
};

export const fetchDefaultGateway = async (): Promise<PaymentGateway | null> => {
  return getOptional(() => authAPI.get('/billing/payment-gateways/default'));
};

export const createPaymentGateway = async (gateway: Partial<PaymentGateway>): Promise<PaymentGateway> => {
  const response = await authAPI.post('/billing/payment-gateways', gateway);
  return response.data;
};

export const updatePaymentGateway = async (id: string, gateway: Partial<PaymentGateway>): Promise<PaymentGateway> => {
  const response = await authAPI.put(`/billing/payment-gateways/${id}`, gateway);
  return response.data;
};

export const deletePaymentGateway = async (id: string): Promise<void> => {
  await authAPI.delete(`/billing/payment-gateways/${id}`);
};

// Billing Policies API
export const fetchBillingPolicy = async (): Promise<BillingPolicy | null> => {
  return getOptional(() => authAPI.get('/billing/policies'));
};

export const createBillingPolicy = async (policy: Partial<BillingPolicy>): Promise<BillingPolicy> => {
  const response = await authAPI.post('/billing/policies', policy);
  return response.data;
};

export const updateBillingPolicy = async (policy: Partial<BillingPolicy>): Promise<BillingPolicy> => {
  const response = await authAPI.put('/billing/policies', policy);
  return response.data;
};

export const upsertBillingPolicy = async (policy: Partial<BillingPolicy>): Promise<BillingPolicy> => {
  const response = await authAPI.post('/billing/policies/upsert', policy);
  return response.data;
};

// Alerts API
export const fetchBillingAlerts = async (isRead?: boolean): Promise<BillingAlert[]> => {
  const params = isRead !== undefined ? { is_read: isRead } : {};
  const response = await authAPI.get('/billing/alerts', { params });
  return response.data;
};

export const markAlertAsRead = async (id: string): Promise<void> => {
  await authAPI.put(`/billing/alerts/${id}/read`);
};

// Reports API
export const fetchFinancialSummary = async (startDate?: string, endDate?: string): Promise<unknown> => {
  const params: Record<string, string> = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await authAPI.get('/billing/reports/summary', { params });
  return response.data;
};

export const fetchRevenueReport = async (startDate?: string, endDate?: string, period?: string): Promise<unknown[]> => {
  const params: Record<string, string> = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (period) params.period = period;
  const response = await authAPI.get('/billing/reports/revenue', { params });
  return response.data;
};

export const fetchInvoiceStatusReport = async (startDate?: string, endDate?: string): Promise<unknown[]> => {
  const params: Record<string, string> = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await authAPI.get('/billing/reports/invoice-status', { params });
  return response.data;
};

